import { DialogModule } from '@angular/cdk/dialog';
import { CommonModule, JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { CustomErrorStateMatcher } from '../../../../core/models/CustomErrorStateMatcher';
import { AesEncDecService } from '../../../../core/services/aes/aes-enc-dec.service';
import { MatTabsModule } from '@angular/material/tabs';

@Component({
  selector: 'app-web-form-dialog-box',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatCardModule,
    ReactiveFormsModule,
    RouterOutlet,
    MatListModule,
    MatIconModule,
    CommonModule,
    DialogModule,
    MatProgressSpinner

  ],
  templateUrl: './web-form-dialog-box.component.html',
  styleUrl: './web-form-dialog-box.component.scss',

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebFormDialogBoxComponent {
  form: FormGroup;
  webFormDetails: any[] = [];

  isFormFieldsVisible: boolean = false;
  isEdit: boolean = false
  editIndex: number | null = null;
  public data = inject<any>(MAT_DIALOG_DATA);
  private localStorage = inject(AesEncDecService)
  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<WebFormDialogBoxComponent>

  ) { }

  ngOnInit(): void {
    const storedWebFormDetails = this.localStorage.getData('orderbenificiary');
    if (storedWebFormDetails != "") {
      const allDetails = JSON.parse(storedWebFormDetails);
      this.webFormDetails = allDetails.filter((detail: any) => detail.sku === this.data.sku);

    }
    this.form = this.fb.group({
      deliver_to_full_name: [''],
      deliver_to_first_name: ['', [Validators.required,Validators.pattern('^[A-Z ]+$')]],
      deliver_to_last_name: ['', Validators.required],
      deliver_to_email: ['', [Validators.required, Validators.email, this.uniqueEmailValidator.bind(this)]],

      sku: [{ value: this.data.sku, disabled: true }, Validators.required],
      quantity: ['', [Validators.required, Validators.min(1)]],
      order_item_name: [{ value: this.data.order_item_name, disabled: true }, Validators.required],
      country: [{ value: this.data.country, disabled: true }, Validators.required]

    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  addWebFormDetail(): void {
    if (this.form.valid) {
      // Concatenate first and last name
    const firstName = this.form.get('deliver_to_first_name')?.value?.trim() || '';
    const lastName = this.form.get('deliver_to_last_name')?.value?.trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();

    // Set the full name into the form
    this.form.patchValue({
      deliver_to_full_name: fullName
    });
      const formValue = {
        ...this.form.getRawValue(), // Get all form values including disabled ones
        sku: this.data.sku,
        order_item_name: this.data.order_item_name
      };

      if (this.editIndex !== null) {
        this.webFormDetails[this.editIndex] = formValue;

      } else {
        this.webFormDetails.push(formValue);
      }
      // Retrieve existing data from local storage
      const storedWebFormDetails = this.localStorage.getData('orderbenificiary');
      let allDetails = [];
      let filteredList1 = []
      if (storedWebFormDetails) {
        allDetails = JSON.parse(storedWebFormDetails);
        filteredList1 = allDetails.filter((item1: any) =>
          !this.webFormDetails.some(item2 => item2.sku === item1.sku)
        );
      }

      this.webFormDetails.forEach(item => {
        filteredList1.push(item);
      });
      // filteredList1.push(this.webFormDetails)
      allDetails.push(formValue);

      // Save updated data back to local storage
      this.localStorage.saveData('orderbenificiary', JSON.stringify(filteredList1));

      this.editIndex = null;
      this.toggleFormFields();
      //this.updateLocalStorage();
    }
  }

  toggleFormFields(): void {
    this.form.reset({
      deliver_to_full_name: '',
      deliver_to_first_name: '',
      deliver_to_last_name: '',
      deliver_to_email: '',
      quantity: '',
      country: this.data.country,
      sku: this.data.sku,
      order_item_name: this.data.order_item_name
    });
    this.isFormFieldsVisible = !this.isFormFieldsVisible;

  }

  editDetail(index: number): void {
    this.editIndex = index;
    this.form.patchValue(this.webFormDetails[index]);
    this.isFormFieldsVisible = true;
    this.isEdit = true;

  }

  deleteDetail(index: number): void {
    const removedDetails = this.webFormDetails.splice(index, 1);

    // Update local storage after deletion
    const storedWebFormDetails = this.localStorage.getData('orderbenificiary');
    if (storedWebFormDetails) {
      let allDetails = JSON.parse(storedWebFormDetails);
      const detailToRemove = removedDetails[0];
      allDetails = allDetails.filter((detail: any) => !(detail.sku === detailToRemove.sku && detail.deliver_to_email === detailToRemove.deliver_to_email));
      this.localStorage.saveData('orderbenificiary', JSON.stringify(allDetails));
    }
  }
  uniqueEmailValidator(control: AbstractControl): ValidationErrors | null {
    if (this.webFormDetails != null && this.webFormDetails.length > 0) {
      const email = control.value.toLowerCase();
      if (this.editIndex != null) {
        const editDetails = this.webFormDetails[this.editIndex];
        const isDuplicate = this.webFormDetails.some(detail => detail.deliver_to_email.toLowerCase() === email && email != editDetails?.deliver_to_email.toLowerCase());
        return isDuplicate ? { uniqueEmail: true } : null;
      }
      else {
        const isDuplicate = this.webFormDetails.some(detail => detail.deliver_to_email.toLowerCase() === email);
        return isDuplicate ? { uniqueEmail: true } : null;
      }
    }
    return null;
  }
}
